import { Box, Button, Typography, useTheme } from '@mui/material';
import StartApplicationActionCard from './ActionCard/StartApplicationActionCard';
import { ActionCardDefinition } from './ActionCard/ActionCardDefinitions';

interface DashboardActionSectionProps {
  mainActionCard: ActionCardDefinition;
  growthAwardActionCard?: ActionCardDefinition;
  stateQuestionsActionCard?: ActionCardDefinition;
  showGrowthAward: boolean;
  showStateQuestions: boolean;
  toolkitTitle: string;
  toolkitDescription: string;
  toolkitButtonLabel: string;
  toolkitPendingApprovalLabel: string;
  toolkitEnabled: boolean;
  onOpenToolkit: () => void;
}

function DashboardActionSection({
  mainActionCard,
  growthAwardActionCard,
  stateQuestionsActionCard,
  showGrowthAward,
  showStateQuestions,
  toolkitTitle,
  toolkitDescription,
  toolkitButtonLabel,
  toolkitPendingApprovalLabel,
  toolkitEnabled,
  onOpenToolkit,
}: DashboardActionSectionProps) {
  const theme = useTheme();
  const topActionCardCount = 1 + (showGrowthAward ? 1 : 0) + (showStateQuestions ? 1 : 0);
  const toolkitDesktopMaxWidth = `min(${topActionCardCount * 780 + (topActionCardCount - 1) * 24}px, calc(100vw - 40px))`;

  return (
    <Box width='100%' maxWidth='80dvw' mx='auto'>
      <Box
        width='100%'
        display='flex'
        paddingTop='20px'
        px={{ xs: 2, md: 0 }}
        flexDirection={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'center', md: 'stretch' }}
        flexWrap={'nowrap'}
        justifyContent='center'
        gap={{ xs: '16px', md: '24px' }}
        flex='0 1 auto'
        position='relative'
      >
        <StartApplicationActionCard {...mainActionCard} />

        {showGrowthAward && growthAwardActionCard != null && (
          <StartApplicationActionCard {...growthAwardActionCard} />
        )}

        {showStateQuestions && stateQuestionsActionCard != null && (
          <StartApplicationActionCard {...stateQuestionsActionCard} />
        )}
      </Box>

      <Box
        width='100%'
        display='flex'
        paddingTop='20px'
        px={{ xs: 2, md: 0 }}
        flexDirection='row'
        alignItems='center'
        justifyContent='center'
        gap='24px'
        flex='0 1 auto'
        position='relative'
      >
        <Box
          sx={{
            backgroundColor: theme.palette.sandShade1.main,
            width: '100%',
            maxWidth: { xs: '100%', md: toolkitDesktopMaxWidth },
            minWidth: 0,
            minHeight: 104,
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: { xs: 'center', md: 'space-between' },
            flexDirection: { xs: 'column', sm: 'row' },
            px: { xs: 2, sm: 3, md: 4 },
            py: 2,
            gap: 2,
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
          }}
        >
          <Box>
            <Typography variant='h4' sx={{ margin: 0 }}>
              {toolkitTitle}
            </Typography>
            <Typography variant='body2' sx={{ marginTop: 0.5 }}>
              {toolkitDescription}
            </Typography>
          </Box>

          {toolkitEnabled ? (
            <Button
              variant='primary'
              size='large'
              onClick={onOpenToolkit}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {toolkitButtonLabel}
            </Button>
          ) : (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                py: 0.5,
                borderRadius: '999px',
                backgroundColor: theme.palette.sandShade2.main,
                color: theme.palette.text.secondary,
                alignSelf: { xs: 'flex-start', sm: 'center' },
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.grey[500],
                  flexShrink: 0,
                }}
              />
              <Typography variant='subtitle3' sx={{ whiteSpace: 'nowrap' }}>
                {toolkitPendingApprovalLabel}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default DashboardActionSection;
